import { useEffect, useState } from 'react';
import axios from 'src/axios';

// Module-level cache + in-flight promise so multiple consumers share one
// request and don't refetch on every mount. The feature-flags response is
// tiny and changes rarely, so caching for the lifetime of the page is fine.
let cached = null;
let inFlight = null;

const DEFAULT_FLAGS = {
  vaEnabled: false,
  // The Copilot is always available: guided walkthroughs need no settings and no
  // external call, so a failed flag fetch must not take it away. Only its AI half
  // fails closed.
  copilotEnabled: true,
  copilotAiEnabled: false,
  sttEnabled: false,
  clinicalInsightsEnabled: true,
  // Clinical Insights ranks on PHENOTYPE. This sub-flag is the only thing that
  // lets recorded pathogenic variants reorder that ranking, and it fails closed
  // in every direction: default false here, false in the schema, false in the
  // env fallback, and false in the endpoint's own catch block. A failed flag
  // fetch must never turn genotype-influenced ranking on (TGCLOUD-1712).
  insightsMolecularBoost: false,
  // Both change displayed clinical output, so a failed flag fetch must leave
  // them OFF — the pre-remediation behaviour is what clinicians are trained
  // on, and switching to the new output on a network blip would be worse than
  // either state (TGCLOUD-1712).
  inheritanceCandidateGate: false,
  cascadeModeAwarePriors: false,
  clinicalSuggestionsEnabled: false,
  // Fails closed: a failed flag fetch must not offer a tool that writes to a
  // clinical record. The route refuses with 403 in that case regardless.
  pedigreeSketchEnabled: false
};

function loadFlags() {
  if (cached) return Promise.resolve(cached);
  if (inFlight) return inFlight;
  inFlight = axios
    .get('/va/feature-flags')
    .then(({ data }) => {
      cached = { ...DEFAULT_FLAGS, ...(data || {}) };
      return cached;
    })
    .catch(() => {
      // Fail open with safe defaults: patient VA off, Copilot present but with no
      // AI, Clinical Insights ON (matches the schema default so existing
      // deployments keep the feature).
      cached = { ...DEFAULT_FLAGS };
      return cached;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

// Force the next call to refetch — e.g. after an admin saves new settings.
export function invalidateVAFeatureFlags() {
  cached = null;
}

// Subscribe to the resolved flags. Returns the current value once loaded;
// `null` while the initial fetch is in flight so consumers can show a
// loading state rather than a misleading "disabled" UI.
export default function useVAFeatureFlags() {
  const [flags, setFlags] = useState(cached);
  useEffect(() => {
    let cancelled = false;
    loadFlags().then((f) => {
      if (!cancelled) setFlags(f);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return flags;
}
